import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";

import { useConfirmPayment } from "features/consultations/hooks/useConfirmPayment";
import { useConsultationServices } from "features/consultations/hooks/useConsultationServices";
import { pathGenerators } from "utils/routes";

import { formatCurrency, formatDate } from "../constants";
import { useDownloadBulkReceipts } from "./useDownloadBulkReceipts";
import { useDownloadReceipt } from "./useDownloadReceipt";

export const usePaymentsActions = ({ filteredPayments }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentForModal, setSelectedPaymentForModal] = useState(null);
  const [loadingPaymentId, setLoadingPaymentId] = useState(null);

  const modalConsultationId = useMemo(() => {
    if (!paymentModalOpen || !selectedPaymentForModal) return null;
    return (
      selectedPaymentForModal?.consultation?.id ||
      selectedPaymentForModal?.consultationId
    );
  }, [paymentModalOpen, selectedPaymentForModal]);

  const { data: modalPaymentsDue, isLoading: isModalPaymentsLoading } =
    useConsultationServices(modalConsultationId);

  const confirmPaymentMutation = useConfirmPayment(modalConsultationId);
  const downloadReceiptMutation = useDownloadReceipt();
  const downloadBulkReceiptsMutation = useDownloadBulkReceipts();

  const handlePaymentClick = useCallback((payment) => {
    if (!payment.patient?.id) {
      console.error("❌ Invalid Payment Data - No Patient ID:", payment);
      toast.error("Invalid payment data - missing patient information");
      return;
    }
    if (!payment.consultation?.id && !payment.consultationId) {
      console.error("❌ Invalid Payment Data - No Consultation ID:", payment);
      toast.error("Invalid payment data - missing consultation information");
      return;
    }
    setSelectedPaymentForModal(payment);
    setPaymentModalOpen(true);
  }, []);

  const handleClosePaymentModal = useCallback(() => {
    setPaymentModalOpen(false);
    setSelectedPaymentForModal(null);
    setLoadingPaymentId(null);
  }, []);

  const handleConfirmPayment = useCallback(
    (paymentDue) => {
      if (selectedPaymentForModal && paymentDue) {
        const consultationDueId = paymentDue.id;
        const patientName = `${
          selectedPaymentForModal.patient?.firstName || ""
        } ${selectedPaymentForModal.patient?.surname || ""}`.trim();

        if (!selectedPaymentForModal.patient?.id) {
          console.error("❌ Payment Confirmation Failed - No Patient ID");
          toast.error("Cannot confirm payment - missing patient information");
          return;
        }

        setLoadingPaymentId(consultationDueId);
        confirmPaymentMutation.mutate(consultationDueId, {
          onSuccess: () => {
            toast.success(`Payment confirmed successfully for ${patientName}`);
            setLoadingPaymentId(null);
            handleClosePaymentModal();
            queryClient.invalidateQueries({ queryKey: ["paymentDetails"] });
            queryClient.invalidateQueries({ queryKey: ["paymentMetrics"] });
          },
          onError: (error) => {
            console.error("❌ Payment Confirmation Failed:", {
              error: error.message,
              consultationDueId,
              patientId: selectedPaymentForModal.patient?.id,
              patientName,
              timestamp: new Date().toISOString(),
            });
            toast.error(`Failed to confirm payment: ${error.message}`);
            setLoadingPaymentId(null);
          },
        });
      } else {
        console.error("❌ Payment Confirmation Failed - Missing Data:", {
          hasSelectedPayment: !!selectedPaymentForModal,
          hasPaymentDue: !!paymentDue,
        });
      }
    },
    [
      selectedPaymentForModal,
      confirmPaymentMutation,
      queryClient,
      handleClosePaymentModal,
    ]
  );

  const handleDownloadReceipt = useCallback(
    (payment) => {
      const consultationId =
        payment.consultation?.id || payment.consultationId;
      const patientId = payment.patient?.id;
      const patientName = `${payment.patient?.firstName || ""} ${
        payment.patient?.surname || ""
      }`.trim();

      if (!consultationId || !patientId) {
        console.error("❌ Receipt Generation Failed - Missing Data:", {
          consultationId: !!consultationId,
          patientId: !!patientId,
          payment,
        });
        toast.error("Missing consultation or patient information");
        return;
      }

      if (!payment.patient?.firstName && !payment.patient?.surname) {
        console.error("❌ Receipt Generation Failed - Invalid Patient Data:", {
          patientId,
          patientObject: payment.patient,
        });
        toast.error("Invalid patient information - cannot generate receipt");
        return;
      }

      downloadReceiptMutation.mutate(
        { consultationId, patientId },
        {
          onSuccess: (blob) => {
            const downloadUrl = window.URL.createObjectURL(
              new Blob([blob], { type: "application/pdf" })
            );
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `receipt-${consultationId}-${patientId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            toast.success(`Receipt downloaded successfully for ${patientName}`);
          },
          onError: () => {
            toast.error("Failed to download receipt");
          },
        }
      );
    },
    [downloadReceiptMutation]
  );

  const handlePatientClick = useCallback(
    (e, patient) => {
      if (e.button === 1 || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      if (!patient?.id) return;

      navigate(pathGenerators.patient.payments(patient.id));
    },
    [navigate]
  );

  const handleBulkDownload = useCallback(
    (selectedItems) => {
      if (!selectedItems?.length) return;
      const ids = selectedItems.map(
        (p) => p.maxPaymentDueId ?? p.consultation?.id ?? p.consultationId
      );
      downloadBulkReceiptsMutation.mutate(ids, {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          link.download = `payment-receipts-${moment().format("YYYY-MM-DD")}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success("Receipts downloaded successfully");
        },
        onError: () => {
          toast.error("Failed to generate receipts. Please try again.");
        },
      });
    },
    [downloadBulkReceiptsMutation]
  );

  const handleExportUnpaid = useCallback(() => {
    const unpaid = filteredPayments.filter(
      (p) => p.status !== "PAID" && p.moneyOwed > 0
    );
    if (!unpaid.length) {
      toast.error("No unpaid records to export");

      return;
    }
    const headers = [
      "Consultation Date",
      "Patient Name",
      "Status",
      "Amount Paid",
      "Money Owed",
      "Total Due",
      "Days Open",
      "Last Payment",
    ];
    const rows = unpaid.map((p) => [
      formatDate(p.consultation?.consultationDate || p.consultationDate),
      `${p.patient?.firstName || ""} ${p.patient?.surname || ""}`.trim(),
      p.status || "",
      formatCurrency(p.totalAmountPaid),
      formatCurrency(p.moneyOwed),
      formatCurrency(p.totalAmountDue),
      p.daysOpen || "-",
      formatDate(p.lastPaymentDate),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `unpaid-payments-${moment().format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${unpaid.length} unpaid record(s)`);
  }, [filteredPayments]);

  const handleExportAnonymous = useCallback(() => {
    const unpaid = filteredPayments.filter(
      (p) => p.status !== "PAID" && p.moneyOwed > 0
    );
    if (!unpaid.length) {
      toast.error("No unpaid records to export");

      return;
    }
    const headers = [
      "Consultation Date",
      "Patient ID",
      "Status",
      "Amount Paid",
      "Money Owed",
      "Total Due",
      "Days Open",
      "Last Payment",
    ];
    const rows = unpaid.map((p) => [
      formatDate(p.consultation?.consultationDate || p.consultationDate),
      p.patient?.id || "-",
      p.status || "",
      formatCurrency(p.totalAmountPaid),
      formatCurrency(p.moneyOwed),
      formatCurrency(p.totalAmountDue),
      p.daysOpen || "-",
      formatDate(p.lastPaymentDate),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `anonymous-payments-report-${moment().format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${unpaid.length} anonymous record(s)`);
  }, [filteredPayments]);

  return {
    paymentModalOpen,
    selectedPaymentForModal,
    loadingPaymentId,
    modalPaymentsDue,
    isModalPaymentsLoading,
    handlePaymentClick,
    handleClosePaymentModal,
    handleConfirmPayment,
    handleDownloadReceipt,
    handlePatientClick,
    handleExportUnpaid,
    handleExportAnonymous,
    handleBulkDownload,
  };
};
